import { LitElement, html, css } from 'lit';

// Module-level counter — stable per-instance ids, SSR-safe (no crypto.randomUUID).
let _fieldId = 0;

const VALID_ORIENTATIONS = new Set(['vertical', 'horizontal']);

export class CrusherField extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    :host([hidden]) {
      display: none;
    }

    /* ---- Vertical layout (default) ---- */
    .field {
      display: grid;
      gap: var(--crusher-spacing-2);
    }

    /* ---- Horizontal layout ---- */
    :host([orientation="horizontal"]) .field {
      grid-template-columns: minmax(120px, auto) 1fr;
      grid-template-rows: auto auto;
      align-items: center;
      column-gap: var(--crusher-spacing-4);
      row-gap: var(--crusher-spacing-2);
    }

    :host([orientation="horizontal"]) .field__label-row {
      grid-column: 1;
      grid-row: 1;
      align-self: center;
    }

    :host([orientation="horizontal"]) .field__control-slot {
      grid-column: 2;
      grid-row: 1;
    }

    :host([orientation="horizontal"]) .field__support {
      grid-column: 1 / -1;
      grid-row: 2;
    }

    /* ---- Label area ---- */
    .field__label-row {
      display: flex;
      align-items: baseline;
      gap: var(--crusher-spacing-1);
    }

    .field__label {
      font-weight: var(--crusher-font-weight-medium);
      font-size: var(--crusher-font-size-sm);
      color: var(--crusher-text-primary);
      line-height: var(--crusher-line-height-normal, 1.5);
      margin: 0;
    }

    :host([disabled]) .field__label {
      color: var(--crusher-text-secondary);
      opacity: 0.6;
    }

    .field__required {
      color: var(--crusher-color-status-failure);
      font-size: var(--crusher-font-size-sm);
      line-height: 1;
    }

    /* ---- Control slot ---- */
    .field__control-slot {
      min-width: 0;
    }

    /* ---- Support text (helper / error) ---- */
    .field__helper {
      display: block;
      font-size: var(--crusher-font-size-xs, 0.75rem);
      color: var(--crusher-text-secondary);
      line-height: var(--crusher-line-height-relaxed, 1.6);
      margin: 0;
    }

    .field__error {
      display: block;
      font-size: var(--crusher-font-size-xs, 0.75rem);
      color: var(--crusher-color-status-failure);
      line-height: var(--crusher-line-height-relaxed, 1.6);
      margin: 0;
    }

    /* ---- Slotted label override ---- */
    ::slotted([slot="label"]) {
      font-weight: var(--crusher-font-weight-medium);
      font-size: var(--crusher-font-size-sm);
      color: var(--crusher-text-primary);
      line-height: var(--crusher-line-height-normal, 1.5);
    }

    /* ---- Slotted helper / error overrides ---- */
    ::slotted([slot="helper"]) {
      font-size: var(--crusher-font-size-xs, 0.75rem);
      color: var(--crusher-text-secondary);
      line-height: var(--crusher-line-height-relaxed, 1.6);
    }

    ::slotted([slot="error"]) {
      font-size: var(--crusher-font-size-xs, 0.75rem);
      color: var(--crusher-color-status-failure);
      line-height: var(--crusher-line-height-relaxed, 1.6);
    }
  `;

  static properties = {
    label:       { type: String, reflect: true },
    required:    { type: Boolean, reflect: true },
    helper:      { type: String, reflect: true },
    error:       { type: String, reflect: true },
    disabled:    { type: Boolean, reflect: true },
    orientation: { type: String, reflect: true },
  };

  constructor() {
    super();
    this.label = '';
    this.required = false;
    this.helper = '';
    this.error = '';
    this.disabled = false;
    this.orientation = 'vertical';

    // Stable per-instance ids — generated once in constructor.
    this._instanceId = ++_fieldId;
    this._labelId   = `crusher-field-label-${this._instanceId}`;
    this._helperId  = `crusher-field-helper-${this._instanceId}`;
    this._errorId   = `crusher-field-error-${this._instanceId}`;
  }

  // ---- Lifecycle ----

  connectedCallback() {
    super.connectedCallback();
    // Wire after first paint. updateComplete resolves post-render.
    this.updateComplete.then(() => this._wireA11y());
  }

  updated(_changed) {
    // Re-wire whenever any property changes — error/helper state affects aria attrs.
    this._wireA11y();
  }

  // ---- Slot helpers ----

  /** Returns the slotted control element (light DOM — querySelector on host, NOT shadow). */
  _slottedControl() {
    return this.querySelector('[slot="control"]');
  }

  _hasSlot(name) {
    return this.querySelector(`[slot="${name}"]`) !== null;
  }

  // ---- A11y wiring ----

  /**
   * Sets aria-labelledby, aria-describedby, and aria-invalid on the slotted control.
   *
   * Called on connectedCallback + updateComplete, updated(), and slotchange so
   * that dynamic slot content is handled correctly.
   *
   * The slotted control lives in light DOM; we reach it via
   * `this.querySelector('[slot="control"]')` — never via shadow root crossing.
   * If the slotted element is a custom element (e.g. crusher-input), we set
   * attributes on its host; the inner shadow input should forward ARIA attrs.
   *
   * NOTE: `disabled` on the host does NOT forward to the slotted control.
   * Different shadow trees prevent attribute forwarding; this is a documented
   * limitation. The label color change is the only visual feedback the wrapper owns.
   */
  _wireA11y() {
    const control = this._slottedControl();
    if (!control) return;

    // aria-labelledby: points to the label-row container id.
    control.setAttribute('aria-labelledby', this._labelId);

    // aria-describedby: error id first (assertive), then helper id.
    const describedBy = [];
    const hasError = this.error || this._hasSlot('error');
    const hasHelper = this.helper || this._hasSlot('helper');

    if (hasError)  describedBy.push(this._errorId);
    if (hasHelper) describedBy.push(this._helperId);

    if (describedBy.length > 0) {
      control.setAttribute('aria-describedby', describedBy.join(' '));
    } else {
      control.removeAttribute('aria-describedby');
    }

    // aria-invalid: true when error present, removed when error cleared.
    if (hasError) {
      control.setAttribute('aria-invalid', 'true');
    } else {
      control.removeAttribute('aria-invalid');
    }
  }

  _onSlotChange() {
    this._wireA11y();
  }

  // ---- Render helpers ----

  _renderLabelRow() {
    const hasLabelSlot = this._hasSlot('label');

    // The label-row container gets the stable _labelId so aria-labelledby resolves.
    if (hasLabelSlot) {
      // Rich label slot — wrapper div carries the id.
      return html`
        <div class="field__label-row" id=${this._labelId}>
          <slot name="label" @slotchange=${this._onSlotChange}></slot>
        </div>
      `;
    }

    if (this.label) {
      return html`
        <div class="field__label-row" id=${this._labelId}>
          <span class="field__label">${this.label}</span>
          ${this.required
            ? html`<span class="field__required" aria-hidden="true">*</span>`
            : ''}
        </div>
      `;
    }

    // No label content — still render the slot and container for future dynamic content.
    return html`
      <div class="field__label-row" id=${this._labelId}>
        <slot name="label" @slotchange=${this._onSlotChange}></slot>
      </div>
    `;
  }

  _renderSupport() {
    const hasErrorSlot = this._hasSlot('error');
    const hasHelperSlot = this._hasSlot('helper');
    const hasError = this.error || hasErrorSlot;
    const hasHelper = this.helper || hasHelperSlot;

    // Error takes visual precedence; if both set, render only error.
    if (hasError) {
      return hasErrorSlot
        ? html`<slot name="error" id=${this._errorId} @slotchange=${this._onSlotChange}></slot>`
        : html`<span id=${this._errorId} class="field__error">${this.error}</span>`;
    }

    if (hasHelper) {
      return hasHelperSlot
        ? html`<slot name="helper" id=${this._helperId} @slotchange=${this._onSlotChange}></slot>`
        : html`<span id=${this._helperId} class="field__helper">${this.helper}</span>`;
    }

    return html``;
  }

  render() {
    const orientation = VALID_ORIENTATIONS.has(this.orientation) ? this.orientation : 'vertical';

    return html`
      <div class="field field--${orientation}">
        ${this._renderLabelRow()}
        <div class="field__control-slot">
          <slot name="control" @slotchange=${this._onSlotChange}></slot>
        </div>
        ${this._renderSupport()}
      </div>
    `;
  }
}

customElements.define('crusher-field', CrusherField);
